import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  CheckCircle2,
  DownloadCloud,
  Package,
  Plus,
  Search,
  X,
} from "lucide-react";
import { Badge, lotStatusBadge, lotTypeBadge } from "@/components/Badge";
import { EmptyState } from "@/components/EmptyState";
import { FilterSelect } from "@/components/FilterSelect";
import { calcLandedCost, useApp, type LotStatus } from "@/context/AppContext";

export const Route = createFileRoute("/lots/")({
  component: LotsListPage,
});

const STATUS_OPTIONS: LotStatus[] = [
  "Unsorted",
  "Sorting",
  "Sorted",
  "Listed",
  "Partially Sold",
  "Closed",
];

const uniqSorted = (vals: string[]): string[] =>
  Array.from(new Set(vals.filter(Boolean))).sort((a, b) => a.localeCompare(b));

function LotsListPage() {
  const navigate = useNavigate();
  const { lots, cards } = useApp();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterGame, setFilterGame] = useState("All");
  const [filterLocation, setFilterLocation] = useState("All");
  const [filterSeller, setFilterSeller] = useState("All");

  // Filter options derived from the lots actually present, so no empty choices
  // show. Card-attribute filtering lives in Inventory, not here.
  const gameOptions = useMemo(
    () => uniqSorted(lots.map((l) => l.game ?? "Pokemon")),
    [lots],
  );
  const locationOptions = useMemo(
    () => uniqSorted(lots.map((l) => l.purchaseLocation ?? "")),
    [lots],
  );
  const sellerOptions = useMemo(
    () => uniqSorted(lots.map((l) => l.seller)),
    [lots],
  );
  // Only worth offering a Seller filter when at least one seller appears on more
  // than one lot — otherwise it's 1:1 with the lots themselves.
  const hasRepeatSeller = useMemo(() => {
    const counts = new Map<string, number>();
    for (const l of lots) {
      const s = l.seller?.trim();
      if (!s) continue;
      counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    return Array.from(counts.values()).some((n) => n > 1);
  }, [lots]);

  const filtered = useMemo(() => {
    let result = [...lots];
    if (filterStatus !== "All")
      result = result.filter((l) => l.status === filterStatus);
    if (filterGame !== "All")
      result = result.filter((l) => (l.game ?? "Pokemon") === filterGame);
    if (filterLocation !== "All")
      result = result.filter((l) => l.purchaseLocation === filterLocation);
    if (filterSeller !== "All")
      result = result.filter((l) => l.seller === filterSeller);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) =>
          l.seller.toLowerCase().includes(q) ||
          l.id.toLowerCase().includes(q) ||
          l.notes.toLowerCase().includes(q),
      );
    }
    return result.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [lots, search, filterStatus, filterGame, filterLocation, filterSeller]);

  // For the per-lot progress bar (inventoried vs estimated).
  const lotInventoryMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const card of cards) {
      if (card.sourceLotId) {
        map[card.sourceLotId] = (map[card.sourceLotId] ?? 0) + card.quantity;
      }
    }
    return map;
  }, [cards]);

  const anyFilterActive =
    search.trim().length > 0 ||
    filterStatus !== "All" ||
    filterGame !== "All" ||
    filterLocation !== "All" ||
    filterSeller !== "All";

  const resetFilters = () => {
    setSearch("");
    setFilterStatus("All");
    setFilterGame("All");
    setFilterLocation("All");
    setFilterSeller("All");
  };

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 border-b border-border pb-3">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Purchase Lots</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void navigate({ to: "/lots/ebay-import" })}
              className="inline-flex items-center gap-1.5 rounded-md border border-secondary/40 bg-secondary/20 px-3 py-2 text-xs font-semibold text-secondary-foreground hover:bg-secondary/30"
            >
              <DownloadCloud className="h-4 w-4" />
              Import from eBay
            </button>
            <button
              type="button"
              onClick={() => void navigate({ to: "/lots/create" })}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-secondary-foreground hover:opacity-90"
              aria-label="New lot"
            >
              <Plus className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2.5">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search lots..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {search.length > 0 && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2 pb-1">
          <FilterSelect
            label="Status"
            value={filterStatus}
            options={STATUS_OPTIONS}
            onChange={setFilterStatus}
          />
          {gameOptions.length > 1 && (
            <FilterSelect
              label="Game"
              value={filterGame}
              options={gameOptions}
              onChange={setFilterGame}
            />
          )}
          {locationOptions.length > 0 && (
            <FilterSelect
              label="Purchase Location"
              value={filterLocation}
              options={locationOptions}
              onChange={setFilterLocation}
            />
          )}
          {hasRepeatSeller && (
            <FilterSelect
              label="Seller"
              value={filterSeller}
              options={sellerOptions}
              onChange={setFilterSeller}
            />
          )}
          {anyFilterActive && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1 self-end rounded-md border border-border bg-muted px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-card hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
              Reset
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {filtered.length === 0 ? (
          <EmptyState
            icon={<Package className="h-8 w-8" />}
            title="No lots found"
            description="Tap + to intake a new purchase lot"
            actionLabel="New Lot"
            onAction={() => void navigate({ to: "/lots/create" })}
          />
        ) : (
          <div className="space-y-2.5">
            {filtered.map((lot) => {
              const landed = calcLandedCost(lot);
              const costPerCard =
                lot.estimatedCards > 0 ? landed / lot.estimatedCards : 0;
              const totalBins = lot.sortingBins.reduce(
                (s, b) => s + b.count,
                0,
              );
              const inventoried = lotInventoryMap[lot.id] ?? 0;
              const isComplete =
                lot.estimatedCards > 0 && inventoried >= lot.estimatedCards;
              const pct =
                lot.estimatedCards > 0
                  ? Math.min(inventoried / lot.estimatedCards, 1)
                  : 0;
              const barColor =
                pct >= 1
                  ? "bg-emerald-500"
                  : pct >= 0.5
                    ? "bg-secondary"
                    : "bg-muted-foreground";
              return (
                <button
                  key={lot.id}
                  type="button"
                  onClick={() =>
                    navigate({ to: "/lots/$id", params: { id: lot.id } })
                  }
                  className={`block w-full rounded-md border bg-card p-3.5 text-left transition-colors hover:bg-card/80 ${
                    isComplete
                      ? "border-l-4 border-l-emerald-500 border-border"
                      : "border-border"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-1">
                      <div className="text-base font-semibold text-foreground">
                        {lot.seller || "Unknown Seller"}
                      </div>
                      {lot.lotType && (
                        <Badge
                          label={lot.lotType}
                          variant={lotTypeBadge(lot.lotType)}
                        />
                      )}
                      <div className="text-xs text-muted-foreground">
                        {lot.purchaseDate}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <div className="text-lg font-bold text-foreground">
                        ${landed.toFixed(2)}
                      </div>
                      <Badge
                        label={lot.status}
                        variant={lotStatusBadge(lot.status)}
                      />
                      {isComplete && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-emerald-700">
                          <CheckCircle2 className="h-2.5 w-2.5" />
                          Complete
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="my-2.5 h-px bg-border" />

                  <div className="flex justify-around">
                    <MetaItem
                      value={lot.estimatedCards.toLocaleString()}
                      label="Est. Cards"
                    />
                    <MetaItem
                      value={`$${costPerCard.toFixed(3)}`}
                      label="Per Card"
                    />
                    <MetaItem
                      value={totalBins.toLocaleString()}
                      label="Sorted"
                    />
                    <MetaItem
                      value={inventoried.toLocaleString()}
                      label="Inventoried"
                      accent={inventoried > 0}
                    />
                  </div>

                  <div className="mt-2 text-center text-[11px] text-muted-foreground">
                    {inventoried.toLocaleString()} inventoried /{" "}
                    {lot.estimatedCards.toLocaleString()} estimated
                  </div>
                  {lot.estimatedCards > 0 && (
                    <div className="mt-1.5 h-[3px] overflow-hidden rounded-full bg-border">
                      <div
                        className={`h-full rounded-full transition-all ${barColor}`}
                        style={{ width: `${pct * 100}%` }}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MetaItem({
  value,
  label,
  accent,
}: {
  value: string;
  label: string;
  accent?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div
        className={`text-[15px] font-semibold ${accent ? "text-secondary-foreground" : "text-foreground"}`}
      >
        {value}
      </div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}
